import { commonShortFieldLabels, commonShortNouns } from "./common-short-fields";

export const examAdminUsers = {
  staffCreateSuccess: "Exam staff created successfully",
  candidateCreateSuccess: "Candidate created successfully",
  createUserSuccess: "User created successfully",
  userUpdated: "User information updated",
  passwordReset: "Password reset",
  accountLocked: "Account locked",
  accountUnlocked: "Account unlocked",
  candidateKickedOut: "Signed out. The account session is now invalid.",
  importCompleted: "Import completed: requested {requested}, created {created}, updated {updated}",
  pageTitleStaff: "Exam Staff Management",
  pageSubtitleStaff:
    "Centrally manage exam staff accounts, roles, and permission responsibilities.",
  pageTitleCandidate: "Candidate Management",
  pageSubtitleCandidate:
    "Manage candidate accounts separately, with create, status edit, and password reset support.",
  rolePermissionsPageTitle: "Role Permissions",
  rolePermissionsPageSubtitle:
    "View the exam-admin role catalog and permission matrix as the current reference for multi-role union access.",
  pageTitleAll: "Users and Roles",
  pageSubtitleAll:
    "Manage exam staff and candidate accounts in one place while moving from seed accounts to operable configuration.",
  createStaff: "Create Exam Staff",
  createCandidate: "Create Candidate",
  createUser: "Create User",
  totalStaff: "Total Staff",
  totalCandidate: "Total Candidates",
  totalUsers: "Total Users",
  activeAccounts: "Active Accounts",
  activeUsers: "Active Users",
  lockedAccounts: "Locked Accounts",
  pendingPasswordChange: "Password Change Required",
  availableRoles: "Available Roles",
  createStaffDrawerTitle: "Create Exam Staff",
  createCandidateDrawerTitle: "Create Candidate",
  createUserDrawerTitle: "Create User",
  editUserDrawerTitle: "Edit User",
  editUserDrawerTitleWithId: "Edit User #{id}",
  resetPasswordDrawerTitle: "Reset Password",
  resetPasswordDrawerTitleWithId: "Reset Password #{id}",
  resetPasswordResultTitle: "Password Reset",
  resetPasswordResultHint:
    "Save the login information below. The plaintext password will no longer be shown after closing.",
  batchResetPasswordResultTitle: "Batch Password Reset Result",
  batchResetPasswordResultHint:
    "Below are the accounts whose passwords were reset successfully. Copy and save them promptly.",
  batchImportModalTitle: "Batch Import Candidates (Excel)",
  importInvalidArray: "Invalid import payload format",
  importInvalidJson: "Failed to parse Excel file",
  importExample: "Use the provided template file for import",
  resetPasswordHelp: "A new login password will be set for {username}.",
  searchPlaceholder: "Search by username or full name",
  searchPlaceholderStaff: "Search exam staff by username or full name",
  searchPlaceholderCandidate: "Search candidates by username or full name",
  summary: {
    totalUsers: "Total users: {count}",
    activeUsers: "Active users: {count}",
    availableRoles: "Available roles: {count}",
  },
  table: {
    id: "ID",
    username: "Username",
    fullName: "Full Name",
    role: "Role",
    status: commonShortNouns.status,
    onlineStatus: "Online Status",
    phone: "Phone",
    idCardNo: "ID Card",
    expiresAt: "Expires At",
    securityState: "Security State",
    superuser: "Superuser",
    lastLogin: "Last Login",
    createdAt: commonShortFieldLabels.createdAt,
    actions: "Actions",
  },
  fields: {
    avatarUrl: "Avatar URL",
    phone: "Phone",
    idCardNo: "ID Card Number",
    expiresAt: "Expires At",
    expiresQuickDay: "1 day",
    expiresQuickWeek: "1 week",
    expiresQuickMonth: "1 month",
    expiresQuickYear: "1 year",
    initialPassword: "Initial Password",
    newPassword: "New Password",
    loginAddress: "Login URL",
    temporaryPassword: "Random Password",
    status: commonShortNouns.status,
    superuser: "Superuser",
  },
  actions: {
    batchImport: "Batch Import",
    resetPassword: "Reset Password",
    batchResetPassword: "Batch reset passwords ({count})",
    batchClearAvatar: "Batch clear avatars ({count})",
    batchLock: "Batch lock ({count})",
    batchUnlock: "Batch unlock ({count})",
    clearAvatar: "Clear avatar",
    lock: "Lock",
    unlock: "Unlock",
    impersonate: "Impersonate",
    kickout: "Sign out",
  },
  labels: {
    superuserShort: "Super Admin",
    online: "Online",
    offline: "Offline",
    identitySourceSso: "Unified Identity",
    identitySourceLocalCandidate: "Local Candidate",
    identitySourceLocalStaff: "Local Staff",
    linked: "Linked",
    unlinked: "Unlinked",
  },
  messages: {
    candidateCustomFieldsSaved: "Candidate custom fields saved",
    batchCustomFieldApplied: "Batch update finished: success {success}, failed {failed}",
    batchLockCompleted: "Batch lock completed: success {success}, failed {failed}",
    batchUnlockCompleted: "Batch unlock completed: success {success}, failed {failed}",
    batchClearAvatarCompleted: "Batch avatar clear finished: success {success}, failed {failed}",
    batchResetPasswordCompleted:
      "Batch password reset completed: success {success}, failed {failed}",
    batchCustomFieldValueRequired: "Configure at least one field value",
    selectExcelFileWarning: "Please select an Excel file first",
    ssoLinkSuccess: "Linked unified-identity candidates: {linked}/{requested}",
  },
  ssoSync: {
    searchPlaceholder: "Search unified-identity candidates by username",
    action: "Link Unified-Identity Candidates",
    staffAction: "Link Unified-Identity Staff",
    bindAction: "Bind Unified Identity",
    unbindAction: "Unlink",
    staffManagedTitle: "Exam staff master data has moved to Unified Identity",
    staffManagedDescription:
      "This page displays staff master data from sso-center. Create, edit, password reset, lock, and related operations should be maintained in Identity and Access in the platform admin.",
    modalTitle: "Link Unified-Identity Candidates",
    modalSubmit: "Link Selected",
    modalAlertTitle: "Link candidates from Unified Identity",
    modalAlertDescription:
      "Only candidate users from Unified Identity are linked. Authentication remains owned by Unified Identity; the exam side only keeps candidate business profiles and exam relationships.",
    bindModalTitle: "Bind unified identity to local user: {username}",
    bindModalSubmit: "Confirm Binding",
    bindModalAlertTitle: "Bind local user to Unified Identity",
    bindModalAlertDescription:
      "After binding, future Unified Identity logins will resolve to this local business user first instead of relying only on username matching.",
    unbindConfirmTitle: "Unlink Unified Identity",
    unbindConfirmDescription:
      "After unlinking, local user {username} will no longer be bound to a Unified Identity account, and future Unified Identity logins will not resolve to this local business user.",
    staffModalTitle: "Link Unified-Identity Staff",
    staffModalSubmit: "Link and Assign Roles",
    staffModalAlertTitle: "Link exam staff from Unified Identity",
    staffModalAlertDescription:
      "After selecting staff from Unified Identity, you can assign multiple local business roles at once. Unified Identity owns accounts and base identity; exam only maintains its own business roles and permission union.",
    remoteRole: "Unified Identity Role",
    linkedStatus: "Link Status",
    identitySource: "Source",
    bindingSummary: "Binding",
    ssoBoundWithId: "Bound to SSO #{id}",
    linkedToLocalUser: "Bound to local user: {username}",
    linkedToLocalUserWithRole: "Bound to local user: {username} ({role})",
    localOnly: "Local only",
    staffLinkSuccess: "Linked unified-identity staff: {linked}/{requested}",
    bindSuccess: "Unified identity binding saved",
    unbindSuccess: "Unified identity link removed",
  },
  importDialog: {
    templateDownload: "Download template",
    validatePreview: "Validate and preview",
    confirmImport: "Confirm import",
    instruction:
      "Download the template, fill it by column, then upload it for validation. Only rows without errors will be imported after confirmation.",
    selectFile: "Select Excel file",
    selectedFile: "Selected: {name}",
    summary: "{requested} rows total: {valid} importable, {invalid} with errors",
    rowNumber: "Row",
    action: "Action",
    actionUpdate: "Update",
    actionCreate: "Create",
    errors: "Errors",
    pass: "Passed",
  },
  customFields: {
    action: "Custom Fields",
    batchAction: "Batch Set Fields ({count})",
    dialogTitle: "Candidate Custom Fields",
    batchDialogTitle: "Batch Set Candidate Custom Fields ({count} selected)",
    preview: "Preview validation",
    apply: "Confirm apply",
    instruction:
      "Select the target fields and values first, run preview validation, and only then apply the changes.",
    targetFields: "Target fields",
    targetFieldsPlaceholder: "Select fields to update in batch",
    emptyHint: "Select fields before entering values.",
    previewSummary: "Validation result: requested {requested}, passed {success}, failed {failed}",
    candidateId: "Candidate ID",
    result: "Result",
    reason: "Reason",
    pass: "Passed",
    fail: "Failed",
  },
  confirm: {
    kickoutTitle: "Sign out this account?",
    kickoutDescription:
      "After sign-out, the current session for this account will be invalidated immediately and login will be required again.",
    resetPasswordTitle: "Reset password?",
    resetPasswordDescription:
      "A random new password will be generated for {username}, and the current session will be invalidated.",
    batchLockTitle: "Lock selected accounts?",
    batchLockDescription:
      "This locks {count} selected candidate accounts and invalidates their current sessions.",
    batchUnlockTitle: "Unlock selected accounts?",
    batchUnlockDescription: "This unlocks {count} selected candidate accounts.",
    batchClearAvatarTitle: "Clear avatars in batch?",
    batchClearAvatarDescription: "This clears the avatar for {count} selected candidates.",
    batchResetPasswordTitle: "Reset selected passwords?",
    batchResetPasswordDescription:
      "This generates random new passwords for {count} selected candidates and invalidates their current sessions.",
  },
};
